import { createAsyncThunk } from "@reduxjs/toolkit"

import { errorSave } from "../../../redux/errors.slice"
import { unknownError } from "../../../utils/handlers/handle-error"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import { partialUpdateEvent } from "../../Calendar/api/services.ts"
import { type PartialUpdateEventData } from "../../Calendar/api/services.types.ts"
import * as services from "../api/services"
import type { Lead, LeadEvent } from "../types/lead.types.ts"
import {
  type CreateLeadParams,
  type LeadFetchFailure,
  type UpdateLeadParams
} from "./lead.types.ts"

export const createLead = createAsyncThunk<Lead, CreateLeadParams>(
  "lead/createLead",
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createLead(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    const lead = response?.data?.details?.data as Lead
    const leadId: string = lead?.id

    if (!leadId) {
      dispatch(errorSave("Lead id is not found"))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Lead created successfully.")

    return lead
  }
)

export const fetchLead = createAsyncThunk<
  Lead,
  string,
  { rejectValue: LeadFetchFailure }
>("lead/fetchLeadDetails", async (params, { dispatch, rejectWithValue }) => {
  const response = await services.getLeadDetails(params)

  if (!response.ok) {
    dispatch(errorSave(response?.data?.details || unknownError))
    return rejectWithValue({ status: response.status ?? null })
  }

  return response.data!
})

export const toggleLeadFavorite = createAsyncThunk<
  { id: string; is_favorite: boolean },
  string
>("lead/toggleLeadFavorite", async (id, { dispatch, rejectWithValue }) => {
  const response = await services.toggleLeadFavorite(id)

  if (!response.ok) {
    dispatch(errorSave(response?.data?.details || unknownError))
    return rejectWithValue(response.data)
  }

  return { id, is_favorite: !!response.data?.is_favorite }
})

export const updateLead = createAsyncThunk<any, UpdateLeadParams>(
  "lead/updateLead",
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.updateLead(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Lead updated successfully.")

    return response?.data?.details?.data
  }
)

export const deleteLead = createAsyncThunk<any, string>(
  "lead/deleteLead",
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.deleteLead(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    showResponseMessage(response, "Lead deleted successfully.")

    return { ...response.data, id }
  }
)

export const updateLeadEvent = createAsyncThunk<
  LeadEvent,
  PartialUpdateEventData
>("lead/updateLeadEvent", async (params, { dispatch, rejectWithValue }) => {
  const response = await partialUpdateEvent(params)

  if (!response.ok) {
    dispatch(errorSave(response?.data?.details || unknownError))
    return rejectWithValue(response.data)
  }

  showResponseMessage(response, "Event updated successfully.")

  return response?.data?.details?.data as unknown as LeadEvent
})
